import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { createTransfer, listTransfers } from '@/lib/api/inventory-transfers.js';
import { listStores } from '@/lib/api/stores.js';
import { listInventoryItems, type InventoryItemListRow } from '@/lib/api/inventory.js';
import { useMultiStore } from '@/hooks/useMultiStore.js';
import { SlideOver } from '@/components/SlideOver.js';
import { formatDate, prettyLabel } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useAuthStore } from '@/stores/auth.js';

export function TransferListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isMultiStore, isLoading: multiStoreLoading } = useMultiStore();
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['inventory-transfers'],
    queryFn: listTransfers,
    enabled: isMultiStore,
  });

  if (!multiStoreLoading && !isMultiStore) {
    return <Navigate to="/inventory" replace />;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#164477]"
        >
          <Plus className="h-4 w-4" />
          New transfer
        </button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-5 py-3">Transfer</th>
              <th className="px-5 py-3">From</th>
              <th className="px-5 py-3">To</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Items</th>
              <th className="px-5 py-3">Date</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data?.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="px-5 py-4 font-mono text-xs">{t.id.slice(0, 8)}…</td>
                <td className="px-5 py-4">{t.fromStoreName}</td>
                <td className="px-5 py-4">{t.toStoreName}</td>
                <td className="px-5 py-4">{prettyLabel(t.status)}</td>
                <td className="px-5 py-4">{t.lineCount}</td>
                <td className="px-5 py-4">{formatDate(t.createdAt)}</td>
                <td className="px-5 py-4 text-right">
                  <Link to={`/inventory/transfers/${t.id}`} className="text-sm font-semibold text-primary">
                    Open
                  </Link>
                </td>
              </tr>
            ))}
            {q.isLoading ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : null}
            {!q.isLoading && q.data?.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                  No transfers
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <SlideOver title="New transfer" open={open} onClose={() => setOpen(false)}>
        <NewTransferForm
          onCreated={(tid) => {
            queryClient.invalidateQueries({ queryKey: ['inventory-transfers'] });
            setOpen(false);
            navigate(`/inventory/transfers/${tid}`);
          }}
        />
      </SlideOver>
    </div>
  );
}

function NewTransferForm({ onCreated }: { onCreated: (id: string) => void }) {
  const storeId = useAuthStore((s) => s.user?.storeId);
  const storesQuery = useQuery({ queryKey: ['stores'], queryFn: listStores });
  const [toStoreId, setToStoreId] = useState('');
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState<Array<{ itemId: string; name: string; qty: string }>>([]);

  const searchQ = useQuery({
    queryKey: ['transfer-item-search', search],
    queryFn: () => listInventoryItems({ search, page: 1, pageSize: 12, sellableOnly: true }),
    enabled: search.trim().length >= 2,
  });

  const mut = useMutation({
    mutationFn: () =>
      createTransfer({
        toStoreId,
        notes: notes || undefined,
        lines: lines.map((l) => ({ itemId: l.itemId, qtyRequested: Number(l.qty) })),
      }),
    onSuccess: (d) => {
      toastSuccess('Transfer requested');
      onCreated(d.id);
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  function addItem(item: InventoryItemListRow) {
    if (lines.some((l) => l.itemId === item.id)) return;
    setLines((p) => [...p, { itemId: item.id, name: item.name, qty: '1' }]);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!toStoreId || lines.length === 0) {
      toastError('Destination and line items required');
      return;
    }
    if (toStoreId === storeId) {
      toastError('Choose a different store');
      return;
    }
    mut.mutate();
  }

  const destOptions = (storesQuery.data ?? []).filter((s) => s.id !== storeId);

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Destination store</span>
        <select
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={toStoreId}
          onChange={(e) => setToStoreId(e.target.value)}
          required
        >
          <option value="">Select…</option>
          {destOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </label>
      {destOptions.length === 0 ? (
        <p className="text-sm text-amber-800">No other stores in tenant — create a second store to transfer.</p>
      ) : null}
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Search items</span>
        <input
          className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </label>
      {searchQ.data?.data.length ? (
        <ul className="max-h-36 overflow-y-auto rounded-xl border border-slate-200">
          {searchQ.data.data.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50"
                onClick={() => addItem(item)}
              >
                {item.name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <ul className="space-y-2">
        {lines.map((l) => (
          <li key={l.itemId} className="rounded-xl border border-slate-200 p-2 text-sm">
            <div className="font-semibold">{l.name}</div>
            <label className="mt-1 block text-xs">
              Qty
              <input
                className="mt-1 w-full rounded border px-2 py-1"
                value={l.qty}
                onChange={(e) =>
                  setLines((p) => p.map((x) => (x.itemId === l.itemId ? { ...x, qty: e.target.value } : x)))
                }
              />
            </label>
            <button
              type="button"
              className="mt-1 text-xs text-red-700"
              onClick={() => setLines((p) => p.filter((x) => x.itemId !== l.itemId))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Notes</span>
        <textarea className="mt-1 w-full rounded-xl border px-3 py-2" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </label>
      <button
        type="submit"
        disabled={mut.isPending || destOptions.length === 0}
        className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-white disabled:opacity-50"
      >
        Submit transfer request
      </button>
    </form>
  );
}
